import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  OnInit,
  DestroyRef,
} from '@angular/core';
import { UserSelectorService } from './user-selector.service';
import { combineLatest, map, scan, startWith, tap } from 'rxjs';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { MultiselectComponent } from '@ui-lib/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'mon-user-selector',
  imports: [TranslateModule, FormsModule, AsyncPipe, MultiselectComponent, ReactiveFormsModule],
  templateUrl: './user-selector.component.html',
  styleUrl: './user-selector.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserSelectorService],
})
export class UserSelectorComponent implements OnInit {
  protected readonly userSelectorService = inject(UserSelectorService);

  private readonly destroyRef = inject(DestroyRef);

  @Input()
  public controlEntity: FormControl<string[]> = new FormControl([], { nonNullable: true });

  @Output()
  public selectionChange: EventEmitter<IDictionaryItem<string>[]> = new EventEmitter<
    IDictionaryItem<string>[]
  >();

  private loadedCount = 0;

  protected readonly users$ = this.userSelectorService.source$.pipe(
    scan(
      (
        acc: IDictionaryItem<string>[],
        newbies: IDictionaryItem<string>[],
      ): IDictionaryItem<string>[] => [...acc, ...newbies],
      [],
    ),
    map((items: IDictionaryItem<string>[]): IDictionaryItem<string>[] => {
      const seenIds = new Set<string>();

      return items.filter((item: IDictionaryItem<string>): boolean => {
        if (!item.id) return false;

        if (seenIds.has(item.id)) {
          return false;
        }

        seenIds.add(item.id);

        return true;
      });
    }),
    tap((items: IDictionaryItem<string>[]): void => {
      this.loadedCount = items.length;
    }),
  );

  public ngOnInit(): void {
    combineLatest([
      this.users$,
      this.controlEntity.valueChanges.pipe(startWith(this.controlEntity.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([items, selectedIds]) => {
        const selectedSet = new Set(selectedIds);
        const selectedItems = items.filter(
          (item) => item.id !== undefined && selectedSet.has(item.id),
        );

        this.selectionChange.emit(selectedItems);
      });
  }

  public loadMore(): void {
    if (this.loadedCount < this.userSelectorService.totalCount()) {
      this.userSelectorService.loadMore();
    }
  }
}
